// A ETIQUETA QUANDO O CLIENTE JA ESTA IDENTIFICADO ([DA-722], pedido dele em 12/09/2026:
// *"quando eu escolho um cliente, ele nao permite selecionar a Etiqueta, nem visualizar as que
// possivelmente possam estar marcadas"*).
//
// ⛔ **Este campo existe porque o bloco de identificacao SOME quando a trava e' satisfeita.** No
// caminho 2 a etiqueta viaja dentro do `capturar-contato`, junto com o telefone; aqui nao ha o que
// capturar — o contato ja existe —, entao **marcar grava na hora**, como a aba Contatos do Dia
// ([DA-691]) e o painel do chat ja fazem.
//
// ⚠️ **A etiqueta mora no CONTATO (jid), nunca na ficha do cliente.** Por isso o campo so' nasce
// quando ha um contato de WhatsApp pra carimbar: cliente do cadastro sem contato nenhum nao tem
// onde a marca colar, e um campo que nao grava seria pior que campo nenhum. Medido em 12/09: dos
// 45 pedidos do caminho 1, so' 6 tem contato vinculado — e' por isso que ele quase sempre nao
// aparece, e isso e' o dado, nao um defeito.
//
// ⛔ **Nao exige nada.** A obrigatoriedade que ele pediu vale no caminho 2 (21 das 71 vendas);
// esticar pra ca travaria as 45 restantes, e trava que barra venda e' decisao dele, nao minha.
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { atendimentoApi } from '../../modules/atendimento/lib/api';
import { useEtiquetas } from '../../modules/atendimento/lib/etiquetas-context';
import { ComboEtiquetas } from './ComboEtiquetas';
import {
  alternar as alternarEtiqueta, marcadasNaTela, ordenarParaOBalcao, type EstadoEtiquetas,
} from './etiquetas-do-balcao';

interface Resposta {
  contato: { id: number; nome: string } | null;
  etiqueta_ids?: number[];
}

interface Props {
  /** A ficha do pedido. `1` (Consumidor) nao resolve contato nenhum. */
  clienteId: number;
  /** O contato que a captura acabou de criar, quando houve uma. Tem precedencia sobre o cliente. */
  contatoId?: number | null;
  /** Quem hospeda ja escreve o rotulo no estilo da propria tela (a tela do fly, 24/09/2026). */
  semRotulo?: boolean;
  /** Repassados ao combo: instrucao curta e altura do campo vizinho (a tela do fly). */
  instrucao?: string;
  alturaMin?: number;
  /** Avisa quem hospeda que o banco mudou — la' a lista do chat se rele ([DA-684]). */
  onGravou?: () => void;
}

export function CampoEtiquetasVenda({ clienteId, contatoId, semRotulo, onGravou, instrucao, alturaMin }: Props) {
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const { paraCampanha, todas, atualizar } = useEtiquetas();
  const [etq, setEtq] = useState<EstadoEtiquetas>({ gravadas: [], novas: [], remover: [] });
  const [contato, setContato] = useState<{ id: number; nome: string } | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // ⛔ O provider e' global mas quem CARREGA a lista sao o Atendimento e a Gestao de Vendas — o
  // caixa nao passa por nenhum dos dois. Sem isto o combo abriria dizendo "nenhuma etiqueta
  // cadastrada" com 28 no banco: ausencia de carga lida como inexistencia.
  useEffect(() => {
    if (todas.length) return;
    atendimentoApi.listarEtiquetas().then(atualizar).catch(() => {});
  }, [todas.length, atualizar]);

  // ⛔ **A ordem sai da peca compartilhada**, nao de um `sort` escrito aqui: ate 12/09/2026 os dois
  // caminhos do modal tinham a propria copia, e a `CONSUMIDOR` no topo ([DA-723]) teria entrado so'
  // num deles — a copia esquecida e' sempre a que ninguem abre ([DA-659]).
  const etiquetas = ordenarParaOBalcao(paraCampanha);

  useEffect(() => {
    let cancelado = false;
    const chave = contatoId ? `contato_id=${contatoId}` : `cliente_id=${clienteId}`;
    if (!contatoId && (!clienteId || clienteId === 1)) { setCarregando(false); setContato(null); return; }
    setCarregando(true);
    api.get<Resposta>(`/api/contatos/identificar?${chave}&inbox=atendimento`)
      .then((r) => {
        if (cancelado) return;
        setContato(r.contato);
        setEtq({ gravadas: r.etiqueta_ids ?? [], novas: [], remover: [] });
      })
      .catch(() => { if (!cancelado) setContato(null); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [clienteId, contatoId]);

  // ⚠️ Grava NA HORA e devolve o estado ao que era se o servidor recusar — otimista com reversao,
  // nao otimista cego: aqui nao ha botao Salvar depois pra consertar.
  const alternar = useCallback((id: number) => {
    if (!contato) return;
    setEtq((antes) => {
      const depois = alternarEtiqueta(antes, id, ehAdmin);
      if (depois === antes) return antes;
      // O que a TELA passou a mostrar decide o que o banco tem que ter — e' a leitura mais simples
      // e cobre os quatro gestos (marcar, desmarcar o que marquei agora, admin tirando uma antiga,
      // admin desfazendo essa retirada).
      const estavaMarcada = marcadasNaTela(antes).includes(id);
      const ficouMarcada = marcadasNaTela(depois).includes(id);
      if (estavaMarcada === ficouMarcada) return depois;
      const corpo = ficouMarcada ? { acrescentar: [id] } : { remover: [id] };
      api.post(`/api/contatos/${contato.id}/etiquetas/marcar`, corpo)
        .then(() => { setErro(null); onGravou?.(); })
        .catch((e: any) => {
          setErro(e?.corpo?.erro ?? 'Nao deu pra gravar a etiqueta.');
          setEtq(antes);
        });
      return depois;
    });
  }, [contato, ehAdmin, onGravou]);

  if (carregando) {
    return (
      <div className="mb-3 text-[11px] text-gray-500 flex items-center gap-1.5">
        <Loader2 size={11} className="animate-spin" /> conferindo as etiquetas do cliente...
      </div>
    );
  }
  // ⛔ Sem contato o campo NAO aparece — e nao e' silencio: a etiqueta cola no WhatsApp, e sem ele
  // nao ha onde gravar. Oferecer o campo aqui seria a tela prometendo o que o servidor nao faz.
  if (!contato) return null;

  return (
    <div className={semRotulo ? '' : 'mb-3'}>
      {/* ⚠️ **Rotulo obrigatorio AQUI, ao contrario do caminho 2**: la o campo vive dentro do bloco
          "Cadastrar o WhatsApp agora" e herda o contexto; aqui ele abre o modal sozinho, e uma
          fileira de pills sem titulo nao diz o que e' — quando ha etiqueta marcada o placeholder
          (que carrega o nome do campo) some. */}
      {!semRotulo && (
        <div className="text-[11px] font-bold text-gray-600 uppercase mb-1">
          Etiquetas do cliente
          <span className="font-normal normal-case text-gray-500"> — a area de atuacao dele</span>
        </div>
      )}
      <ComboEtiquetas
        etiquetas={etiquetas}
        estado={etq}
        onAlternar={alternar}
        ehAdmin={ehAdmin}
        instrucao={instrucao}
        alturaMin={alturaMin}
      />
      {erro && <div className="text-[11px] text-red-700 mt-1">{erro}</div>}
    </div>
  );
}
